/**
 * Parses a Capacitor `appUrlOpen` deep-link URL for the password-reset flow.
 *
 * Native reset emails redirect to `com.malkkov.volyx://reset-password?code=...`
 * (PKCE) or `...?token_hash=...&type=recovery` (OTP). This extracts whichever
 * credential is present and returns `null` for any URL that is not the
 * reset-password deep link so unrelated links are ignored.
 */

export interface ResetPasswordDeepLink {
  /** PKCE authorization code to exchange for a session, if present. */
  code: string | null;
  /** OTP token hash to verify (works cross-device, no code verifier needed). */
  tokenHash: string | null;
  /** Provider error description, if the redirect carried one. */
  errorDescription: string | null;
}

export function parseResetPasswordDeepLink(url: string): ResetPasswordDeepLink | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  // For `com.malkkov.volyx://reset-password`, host is `reset-password` and
  // pathname is empty. Match on the combined value to stay scheme-agnostic.
  const combined = `${parsed.host}${parsed.pathname}`.replace(/\/+$/, '');
  if (!combined.endsWith('reset-password')) {
    return null;
  }

  // Some providers place the recovery tokens in the URL hash (implicit flow).
  const hashParams = new URLSearchParams(parsed.hash.replace(/^#/, ''));
  const params = parsed.searchParams;

  return {
    code: params.get('code'),
    tokenHash: params.get('token_hash') ?? hashParams.get('token_hash'),
    errorDescription: params.get('error_description') ?? hashParams.get('error_description'),
  };
}
